import { TVNavParams } from '@euler/app/Routes.tv';
import { LinkButton } from '@euler/app/components/LinkButton';
import { Typography } from '@euler/app/components/typography';
import { WiredMemberAvatar } from '@euler/app/tv/components/MemberAvatar';
import { TaskDefectiveChartView } from '@euler/app/tv/components/TaskDefectiveChartView';
import { WiredTaskInspectionScoreView } from '@euler/app/tv/components/inspection/WiredInspectionScoreView';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useDeviceType } from '@euler/functions/useDeviceType';
import { DefectiveLevel } from '@euler/model';
import { TaskDetail } from '@euler/model/task-detail';
import { formatDateTime } from '@euler/utils/formatters';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { DeviceType } from 'expo-device';
import { memo, useCallback } from 'react';
import { Image, StyleProp, View, ViewStyle } from 'react-native';

export const InspectionOverview = memo(
  ({
    style,
    detail,
    flowId,
    onChartCellPress,
  }: {
    style?: StyleProp<ViewStyle>;
    detail: TaskDetail;
    flowId?: string;
    onChartCellPress?: (defectiveLevel: DefectiveLevel) => void;
  }) => {
    const navigation = useNavigation<StackNavigationProp<TVNavParams>>();
    const flow = flowId
      ? detail.inspection.flows.find(x => x.id === flowId)
      : undefined;
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const memberId = flow?.assignedTo.id;
    const technicianName = flow?.assignedTo.name;
    const timestamp = flow?.finishedAt ?? flow?.updatedAt ?? flow?.createdAt;
    const deviceType = useDeviceType();

    const onMemberPress = useCallback(() => {
      if (memberId == null) return;
      navigation.navigate('_memberDetail', { targetMemberId: memberId });
    }, [memberId, navigation]);

    return (
      <HStack
        justifyContent="space-between"
        alignItems="stretch"
        css={`
          padding: 0 ${rpx(16)}px;
        `}
        style={style}
      >
        <View
          css={`
            width: ${rpx(400)}px;
            justify-content: center;
          `}
        >
          <LayoutProviderView>
            <WiredTaskInspectionScoreView />
          </LayoutProviderView>

          <View
            css={`
              height: 1px;
              width: ${rpx(40)}px;
              background-color: ${theme.task.inspection.techInfo.barColor};
              margin-top: ${rpx(90)}px;
              margin-bottom: ${rpx(32)}px;
              margin-left: ${rpx(32)}px;
            `}
          />

          <HStack justifyContent="flex-start">
            <WiredMemberAvatar
              memberId={memberId}
              size={rpx(120)}
              baseStyle={{ marginLeft: rpx(12) }}
              css={`
                border-radius: ${rpx(20)}px;
              `}
            />
            <View
              css={`
                margin-left: ${rpx(20)}px;
              `}
            >
              <Typography.Label regular size={rpx(32)} lineHeight={rpx(36)}>
                <Typography.Label
                  regular
                  size={rpx(32)}
                  lineHeight={rpx(36)}
                  color={theme.task.inspection.techInfo.nameLabelTextColor}
                >
                  检测技师:{' '}
                </Typography.Label>
                <Typography.Label
                  bold
                  size={rpx(32)}
                  lineHeight={rpx(36)}
                  color={theme.task.inspection.techInfo.nameTextColor}
                >
                  {technicianName ?? '未派工'}
                </Typography.Label>
              </Typography.Label>
              {timestamp != null && (
                <Typography.Label
                  light
                  size={rpx(24)}
                  color={theme.task.inspection.techInfo.timeTextColor}
                  lineHeight={rpx(36)}
                >
                  {formatDateTime(timestamp)}
                </Typography.Label>
              )}
              {technicianName != null && (
                <LinkButton
                  text="查看技师简介"
                  textStyle={{
                    fontSize: rpx(24),
                    color: theme.task.inspection.techInfo.detailLinkTextColor,
                    lineHeight: rpx(36),
                  }}
                  css={`
                    justify-content: flex-start;
                  `}
                  onPress={onMemberPress}
                >
                  <Icon
                    name="chevron-right"
                    size={rpx(20)}
                    color={theme.task.inspection.techInfo.detailLinkTextColor}
                  />
                </LinkButton>
              )}
            </View>
          </HStack>
        </View>

        <Center
          css={`
            flex: 1;
            align-self: stretch;
          `}
        >
          <Image
            source={require('@euler/assets/img/car.png')}
            resizeMode="contain"
            css={`
              left: ${rpx(20)}px;
            `}
            style={{
              width: deviceType === DeviceType.PHONE ? '85%' : '100%',
              height: undefined,
              aspectRatio: 779 / 469,
            }}
          />
        </Center>

        <View
          css={`
            width: ${rpx(640)}px;
            justify-content: center;
          `}
        >
          <TaskDefectiveChartView
            detail={detail}
            showZero
            alwaysEnabled
            showIcons
            size={rpx(112)}
            strokeWidth={rpx(10)}
            onCellPress={onChartCellPress}
            itemStyle={{
              backgroundColor: theme.panel.bg,
              width: rpx(280),
              height: rpx(280),
              marginLeft: rpx(40),
              marginBottom: rpx(40),
              borderRadius: rpx(20),
            }}
            labelContainerStyle={{
              marginTop: rpx(46),
            }}
            labelStyle={{
              fontSize: rpx(24),
              lineHeight: rpx(30),
              marginLeft: rpx(18),
            }}
            iconSize={rpx(24)}
            css={`
              flex-wrap: wrap;
            `}
          />
        </View>
      </HStack>
    );
  },
);
